import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createSubscription, updateSubscriptionFields } from '../services/subscriptionService';
import { useOrganization } from '../contexts/OrganizationContext';
import { useLocation } from 'react-router-dom';

import {
    Box,
    TextField,
    MenuItem,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
} from '@mui/material';

const ManageSubscription = ({ }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const subscription = location.state?.subscription || null;

    const { refreshSubscriptions, subscriptions } = useOrganization();

    // Determine edit mode based on the presence of subscriptionGuid
    const isEditMode = !!subscription;

    // Initialize state for form fields
    const [orgName, setOrgName] = useState(subscription?.orgName || '');
    const [selectedPlan, setSelectedPlan] = useState(subscription?.planId || 'free');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Update fields when switching to edit mode or when subscription changes
    useEffect(() => {
        if (isEditMode) {
            setOrgName(subscription?.orgName || '');
            setSelectedPlan(subscription?.planId || 'free');
        }
    }, [isEditMode, subscription]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!orgName.trim()) {
            alert('Organization name is required!');
            return;
        }

        // Retrieve accountGuid from localStorage
        const accountGuid = localStorage.getItem('authSession')
            ? JSON.parse(localStorage.getItem('authSession')).accountGuid
            : null;

        if (!accountGuid) {
            alert('Account session not found. Please log in again.');
            setIsSubmitting(false);
            return;
        }

        setIsSubmitting(true);

        try {
            if (isEditMode) {
                // Check if selecting Free plan while another Free plan exists
                const hasFreePlan = subscriptions.some(
                    (sub) =>
                        sub.planId === 'free' &&
                        sub.subscriptionGuid !== subscription.subscriptionGuid && // Exclude current subscription
                        !sub.canceledAt
                );

                if (selectedPlan === 'free' && hasFreePlan) {
                    alert(
                        'You can only have one Free Plan per account. Please select a different plan.'
                    );
                    setIsSubmitting(false);
                    return;
                }

                // Editing an existing subscription
                const updatedFields = {
                    orgName,
                    planId: selectedPlan,
                    updatedAt: new Date().toISOString(),
                };

                await updateSubscriptionFields(subscription.subscriptionGuid, updatedFields);
                alert(`Organization "${orgName}" updated successfully.`);
            } else {
                // Creating a new subscription
                const hasFreePlan = subscriptions.some(
                    (sub) => sub.planId === 'free' && !sub.canceledAt
                );

                if (selectedPlan === 'free' && hasFreePlan) {
                    alert('You can only have one Free Plan per account. Please select a different plan.');
                    setIsSubmitting(false);
                    return;
                }

                const subscriptionData = {
                    accountGuid,
                    orgName,
                    planId: selectedPlan,
                };

                const subscriptionResponse = await createSubscription(subscriptionData);
                if (subscriptionResponse && subscriptionResponse.subscriptionGuid) {
                    alert(`Organization "${orgName}" created with the "${selectedPlan}" plan.`);
                } else {
                    alert('Failed to create the organization. Please try again.');
                }
            }

            await refreshSubscriptions();
            navigate('/billing');
        } catch (error) {
            console.error('Error managing subscription:', error);
            alert('An error occurred. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                maxWidth: '600px',
                margin: '0 auto',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                textAlign: 'center',
            }}
        >
            <Typography variant="h4" component="h1">
                {isEditMode ? 'Edit Subscription' : 'Add a New Organization'}
            </Typography>
            <TextField
                label="Organization Name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                fullWidth
                required
                autoComplete="off"
                disabled={isSubmitting}
            />
            <FormControl fullWidth>
                <InputLabel id="select-plan-label">Select Plan</InputLabel>
                <Select
                    labelId="select-plan-label"
                    value={selectedPlan}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    label="Select Plan"
                    disabled={isSubmitting}
                >
                    <MenuItem value="free">Free Plan</MenuItem>
                    <MenuItem value="pro">Pro Plan</MenuItem>
                </Select>
            </FormControl>
            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>

                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate(-1)}
                    sx={{ marginRight: 2 }}
                    disabled={isSubmitting}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit}
                    //sx={{ marginTop: 2 }}
                    disabled={isSubmitting}
                >
                    {isSubmitting
                        ? isEditMode
                            ? 'Updating...'
                            : 'Creating...'
                        : isEditMode
                            ? 'Update Subscription'
                            : 'Create Organization'}
                </Button>
            </Box>
        </Box>
    );
};

export default ManageSubscription;


/*
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createSubscription } from '../services/subscriptionService';
import { useOrganization } from '../contexts/OrganizationContext';

import {
    Box,
    TextField,
    MenuItem,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
} from '@mui/material';

const ManageSubscription = () => {
    const navigate = useNavigate();
    const { refreshSubscriptions, subscriptions } = useOrganization();
    const [orgName, setOrgName] = useState('');
    const [selectedPlan, setSelectedPlan] = useState('free');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!orgName.trim()) {
            alert('Organization name is required!');
            return;
        }

        // Retrieve accountGuid from localStorage
        const accountGuid = localStorage.getItem('authSession')
            ? JSON.parse(localStorage.getItem('authSession')).accountGuid
            : null;

        if (!accountGuid) {
            alert('Account session not found. Please log in again.');
            setIsSubmitting(false);
            return;
        }

        setIsSubmitting(true);

        try {
            // Check if a free plan already exists
            const hasFreePlan = subscriptions.some(
                (sub) => sub.planId === 'free' && !sub.canceledAt
            );

            if (selectedPlan === 'free' && hasFreePlan) {
                alert('You can only have one Free Plan per account. Please select a different plan.');
                setIsSubmitting(false);
                return;
            }

            const subscriptionData = {
                accountGuid,
                orgName,
                planId: selectedPlan,
            };

            const subscriptionResponse = await createSubscription(subscriptionData);
            if (subscriptionResponse && subscriptionResponse.subscriptionGuid) {
                refreshSubscriptions();
                alert(`Organization "${orgName}" created with the "${selectedPlan}" plan.`);
                navigate('/billing');
            } else {
                alert('Failed to create the organization. Please try again.');
            }
        } catch (error) {
            console.error('Error creating organization:', error);
            alert('An error occurred while creating the organization. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                maxWidth: '600px',
                margin: '0 auto',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                textAlign: 'center',
            }}
        >
            <Typography variant="h4" component="h1">
                Add a New Organization
            </Typography>
            <TextField
                label="Organization Name"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                fullWidth
                required
                autoComplete='off'
                disabled={isSubmitting}
            />
            <FormControl fullWidth>
                <InputLabel id="select-plan-label">Select Plan</InputLabel>
                <Select
                    labelId="select-plan-label"
                    value={selectedPlan}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    label="Select Plan"
                    disabled={isSubmitting}
                >
                    <MenuItem value="free">Free Plan</MenuItem>
                    <MenuItem value="pro">Pro Plan</MenuItem>
                </Select>
            </FormControl>
            <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                sx={{ marginTop: 2 }}
                disabled={isSubmitting}
            >
                {isSubmitting ? 'Creating...' : 'Create Organization'}
            </Button>
        </Box>
    );
};

export default ManageSubscription;
*/